import { validateSelection } from './report-schedule.js';
/**
 * A report run ON DEMAND, and the preview of what any report will contain (Michael,
 * 2026-10-07): "make it the 'reports' tab where we can either create a scheduled
 * report, or run a one off report ... as someone is creating what the report should
 * include, have a visual to show what the report will look like and what it will
 * contain."
 *
 * 🔥 THE PREVIEW IS THE REPORT, NOT A PICTURE OF ONE. Everything here is produced by
 * the SAME query and the SAME column spec the emailed workbook is built from — see
 * `report-data.ts` in the API. A preview assembled from mock rows, or from its own
 * idea of which columns exist, would be worse than no preview at all: it would answer
 * "what will I get?" confidently and wrongly, and nobody would find out until a week
 * of payroll had been read off it.
 *
 * WHICH IS WHY THE ROW CAP IS A CAP, NEVER A FILTER. The preview shows the first
 * {@link REPORT_PREVIEW_ROW_LIMIT} rows of each tab and states the true total beside
 * them. "15 of 412 rows" is honest; silently showing 15 and letting someone conclude
 * the report is small is not.
 *
 * THIS MODULE IS PURE — shapes and validation only. It is imported by the web app, the
 * API and the hourly job, so it must not reach for a clock, a database or a timezone.
 */
/** How many time-entry rows per tab the preview carries. */
export const REPORT_PREVIEW_ROW_LIMIT = 15;
/** How many rows of each extra section (PTO, bonus, …) the preview carries. */
export const REPORT_PREVIEW_SECTION_ROW_LIMIT = 8;
/**
 * How many tabs the preview renders when a report is split by supervisor.
 *
 * A supervisor split over the whole company is ~25 tabs; rendering every one turns the
 * preview into the report. The count of tabs is always stated in the summary, so the
 * cap removes detail, never the fact that the detail exists.
 */
export const REPORT_PREVIEW_GROUP_LIMIT = 4;
/**
 * The longest window a one-off report may cover.
 *
 * A bound, not a policy: `scope: all` over a year is every punch the company has ever
 * recorded, assembled in one request, and a mistyped year is the easy way to ask for
 * it. Roughly a quarter covers every real question ("this month", "last payroll run",
 * "the quarter") and keeps a typo cheap.
 */
export const MAX_REPORT_RUN_DAYS = 100;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Whole days between two `YYYY-MM-DD` dates, inclusive of both ends. */
export function inclusiveDayCount(startDate, endDate) {
    const a = Date.parse(`${startDate}T00:00:00Z`);
    const b = Date.parse(`${endDate}T00:00:00Z`);
    if (Number.isNaN(a) || Number.isNaN(b)) {
        return 0;
    }
    return Math.floor((b - a) / 86_400_000) + 1;
}
/**
 * Validate a one-off run.
 *
 * Shares {@link validateSelection} with the schedule validator, so the two cannot
 * disagree about what a legal report contains, and adds only what is unique to a
 * one-off: a real, forward-ordered, bounded window.
 *
 * An INVERTED range is called out by name rather than quietly returning nothing. A
 * report that renders zero rows because the dates were swapped looks exactly like a
 * week in which nobody clocked in.
 */
export function validateReportRun(request) {
    const problems = [];
    if (request.name.trim() === '') {
        problems.push({ field: 'name', message: 'Give the report a name.' });
    }
    if (!ISO_DATE.test(request.startDate) || !ISO_DATE.test(request.endDate)) {
        problems.push({ field: 'dates', message: 'Pick a start and end date.' });
    }
    else if (request.endDate < request.startDate) {
        problems.push({ field: 'dates', message: 'The end date is before the start date.' });
    }
    else {
        const days = inclusiveDayCount(request.startDate, request.endDate);
        if (days > MAX_REPORT_RUN_DAYS) {
            problems.push({
                field: 'dates',
                message: `That range covers ${String(days)} days. Run at most ` +
                    `${String(MAX_REPORT_RUN_DAYS)} days at a time.`,
            });
        }
    }
    problems.push(...validateSelection(request));
    return problems;
}
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_ABBR = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
/**
 * `2026-10-07` → `Wed 7 Oct 2026`.
 *
 * Spelled out rather than left as an ISO date because the preview is read as a
 * document: `2026-10-01 – 2026-10-07` does not tell anyone it is a Wed–Tue payroll
 * week, and the weekday is the fastest way to notice a range that starts on the wrong
 * day. Hand-formatted rather than via `Intl` so the API, the job and the browser cannot
 * produce three different strings for one date.
 */
export function formatReportDate(iso) {
    if (!ISO_DATE.test(iso)) {
        return iso;
    }
    const ms = Date.parse(`${iso}T00:00:00Z`);
    if (Number.isNaN(ms)) {
        return iso;
    }
    const d = new Date(ms);
    const day = DAY_ABBR[d.getUTCDay()] ?? '';
    const month = MONTH_ABBR[d.getUTCMonth()] ?? '';
    return `${day} ${String(d.getUTCDate())} ${month} ${String(d.getUTCFullYear())}`;
}
/** `Wed 1 Oct 2026 – Tue 7 Oct 2026`, or a single date when the range is one day. */
export function formatReportPeriod(startDate, endDate) {
    if (startDate === endDate) {
        return formatReportDate(startDate);
    }
    return `${formatReportDate(startDate)} – ${formatReportDate(endDate)}`;
}
//# sourceMappingURL=report-run.js.map